import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MIcon } from '../../../lib/micon';
import type { Schueler } from '../../../types';
import type { Ueberblick } from './profil-api';
import { aufzaehlung, datum, initialen, tm, wtTm } from './format';
import { EINTRAGEN, SchnellAktion } from './Eintragen';
import { oeffneBlatt } from '../../../lib/nav';

export type { SchnellAktion } from './Eintragen';
export interface MenuEintrag { label: string; icon: string; onClick: () => void; gefahr?: boolean; text?: string }

// Welle P1b/P1d: Kopf des Schülerprofils — Avatar, Name, „bei dir in …“.
// PC: Chips aus dem Überblick, „Über … erzählen“ und „Eintragen“ (Menü mit denselben fünf
// Einträgen wie am Handy). Handy: nur Kopf und ⋯-Menü — „Steht an“ steht direkt darunter,
// eingetragen wird über die feste Leiste unten.
export function ProfilKopf({ s, daten, isMobile, onAktion, onDiktat, onTermin, onBearbeiten, menu }: {
  s: Schueler;
  daten: Ueberblick | null;
  isMobile: boolean;
  onAktion: (a: SchnellAktion) => void;
  onDiktat: () => void;
  onTermin: () => void;
  onBearbeiten: () => void;
  menu: MenuEintrag[];
}) {
  const k = daten?.kopf;
  const vorname = k?.vorname ?? s.name.split(' ')[0];
  const faecher = k?.faecher_bei_mir ?? [];
  const klasse = k?.klasse ?? s.klasse;
  const meta = isMobile
    ? [klasse ? `Klasse ${klasse}` : null, faecher.length ? `bei dir in ${aufzaehlung(faecher)}` : null].filter(Boolean).join(', ')
    : `${klasse ? `Klasse ${klasse}` : ''}${faecher.length ? ` · bei dir in ${aufzaehlung(faecher)}` : ''}`;
  const chips = isMobile ? [] : chipListe(daten);

  return (
    <section className="ld-pk" aria-label={`Profil von ${s.name}`}>
      <div className="ld-pk__haupt">
        <div className="ld-pk__avatar" aria-hidden="true">
          {s.foto ? <img src={s.foto} alt="" /> : initialen(k?.vorname, k?.nachname, s.name)}
        </div>
        <div className="ld-pk__id">
          <h2 className="ld-pk__name">{k?.name ?? s.name}</h2>
          {meta && <div className="ld-pk__meta">{meta}</div>}
          {chips.length > 0 && (
            <div className="ld-pk__chips">
              {chips.map(c => (
                <span key={c.key} className={`ld-pk-chip ld-pk-chip--${c.ton}`}>
                  <MIcon name={c.icon} size={15} />{c.text}
                </span>
              ))}
            </div>
          )}
        </div>
        {!isMobile && (
          <div className="ld-pk__rechts">
            <div className="ld-pk__oben">
              <button type="button" className="ld-pk-diktat" onClick={onDiktat}>
                <MIcon name="mic" size={18} />Über {vorname} erzählen
              </button>
              <Menu eintraege={EINTRAGEN.map(e => ({ label: e.label, icon: e.icon, text: e.text(vorname), onClick: () => onAktion(e.key) }))}
                ausloeser={<><MIcon name="add" size={18} />Eintragen<MIcon name="expand_more" size={18} /></>} ausloeserKlasse="ld-pk-eintragen" breit />
              {/* Welle B4: Blatt fürs Gespräch — sichtbar statt im ⋯-Menü */}
              <button type="button" className="ld-pk-icon" onClick={() => oeffneBlatt({ schuelerId: s.id, name: k?.name ?? s.name })} aria-label="Blatt fürs Gespräch" title="Blatt fürs Gespräch"><MIcon name="description" size={19} /></button>
              <button type="button" className="ld-pk-icon" onClick={onTermin} aria-label="Termin vorschlagen" title="Termin vorschlagen"><MIcon name="calendar_add_on" size={19} /></button>
              <button type="button" className="ld-pk-icon" onClick={onBearbeiten} aria-label="Stammdaten bearbeiten" title="Stammdaten bearbeiten"><MIcon name="edit" size={18} /></button>
              <Menu eintraege={menu} />
            </div>
          </div>
        )}
        {isMobile && <div className="ld-pk__menu-mobil"><Menu eintraege={[{ label: 'Termin vorschlagen', icon: 'calendar_add_on', onClick: onTermin }, { label: 'Stammdaten bearbeiten', icon: 'edit', onClick: onBearbeiten }, ...menu]} /></div>}
      </div>
    </section>
  );
}

type ChipAnzeige = { key: string; ton: 'rot' | 'amber' | 'neutral'; icon: string; text: string };
function chipListe(daten: Ueberblick | null): ChipAnzeige[] {
  if (!daten) return [];
  const aus: ChipAnzeige[] = [];
  for (const c of daten.kopf.chips) {
    if (c.art === 'paragraph68') aus.push({ key: c.art, ton: 'rot', icon: 'warning', text: '§68-Schwelle überschritten' });
    if (c.art === 'attest_offen') aus.push({ key: c.art, ton: 'amber', icon: 'description',
      text: c.anzahl === 1 ? `Attest für ${tm(c.datum)} fehlt` : `${c.anzahl} Atteste fehlen` });
    if (c.art === 'attestpflicht') aus.push({ key: c.art, ton: 'amber', icon: 'assignment_late',
      text: c.seit ? `Attestpflicht seit ${datum(c.seit)}` : 'Attestpflicht' });
    if (c.art === 'termin' && daten.naechster_termin) aus.push({ key: c.art, ton: 'neutral', icon: 'event',
      text: `${daten.naechster_termin.titel} ${wtTm(daten.naechster_termin.datum)}` });
  }
  return aus;
}

// Menü als Portal (U8/H6-Regel: Overlays nicht im Kartenfluss). Ohne `ausloeser` der ⋯-Knopf.
function Menu({ eintraege, ausloeser, ausloeserKlasse, breit = false }: {
  eintraege: MenuEintrag[]; ausloeser?: React.ReactNode; ausloeserKlasse?: string; breit?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  useLayoutEffect(() => {
    if (!offen || !knopf.current) return;
    const r = knopf.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  }, [offen]);
  return (
    <>
      {ausloeser
        ? <button ref={knopf} type="button" className={ausloeserKlasse} aria-haspopup="menu" aria-expanded={offen} onClick={() => setOffen(o => !o)}>{ausloeser}</button>
        : <button ref={knopf} type="button" className="ld-pk-icon" aria-label="Weitere Aktionen" aria-expanded={offen} onClick={() => setOffen(o => !o)}>
            <MIcon name="more_vert" size={19} />
          </button>}
      {offen && pos && createPortal(
        <>
          <div className="ld-pk-menu__hinter" onClick={() => setOffen(false)} />
          <div className={`ld-pk-menu${breit ? ' ld-pk-menu--breit' : ''}`} role="menu" style={{ top: pos.top, right: pos.right }}>
            {eintraege.map(e => (
              <button key={e.label} role="menuitem" type="button" className={`ld-pk-menu__item${e.gefahr ? ' ld-pk-menu__item--gefahr' : ''}${e.text ? ' ld-pk-menu__item--zwei' : ''}`}
                onClick={() => { setOffen(false); e.onClick(); }}>
                <MIcon name={e.icon} size={e.text ? 20 : 17} />
                {e.text ? <span><b>{e.label}</b><small>{e.text}</small></span> : e.label}
              </button>
            ))}
          </div>
        </>, document.body)}
    </>
  );
}
